import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ChevronDown from "../../../components/Atomos/Icons/ChevronDown";
import Copy from "../../../components/Atomos/Icons/Copy";
import Email from "../../../components/Atomos/Icons/Email";
import Global from "../../../components/Atomos/Icons/Global";
import LocationPin from "../../../components/Atomos/Icons/LocationPin";
import Facebook from "../../../components/Atomos/Icons/ViewMode/Facebook";
import Instagram from "../../../components/Atomos/Icons/ViewMode/Instagram";
import Linkedin from "../../../components/Atomos/Icons/ViewMode/Linkedin";
import Whatsapp from "../../../components/Atomos/Icons/ViewMode/Whatsapp";
import XCircled from "../../../components/Atomos/Icons/XCircled";
import {
  getSocialUrl,
  resolveSocialExternalUrl,
  type PersonSocialDisplayName,
} from "../../../components/Organismos/Person/personForm/socialNetworks";
import { toastSuccess } from "../../../utils/toast";
import { getBandForPercent } from "./networkingMapBands";
import NetworkingMapLanguageIcon from "./networkingMapLanguageIcon";
import type { NetworkingMapNode } from "./networkingMapTypes";

const VISIBLE_SPECIALTY_COUNT = 3;
const PANEL_LINK_CLASS =
  "transition-colors hover:text-primary hover:underline";

const PANEL_SECTION_BOX =
  "mt-4 flex w-full flex-col items-start gap-3 self-stretch rounded-lg bg-[#EBEBEB] p-4";

type SocialType =
  | "linkedIn"
  | "x"
  | "facebook"
  | "instagram"
  | "whatsapp";

const SOCIAL_ITEMS: Array<{
  type: SocialType;
  displayName: PersonSocialDisplayName;
  icon: JSX.Element;
}> = [
  {
    type: "linkedIn",
    displayName: "LinkedIn",
    icon: <Linkedin />,
  },
  {
    type: "x",
    displayName: "X",
    icon: <XCircled />,
  },
  {
    type: "facebook",
    displayName: "Facebook",
    icon: <Facebook />,
  },
  {
    type: "instagram",
    displayName: "Instagram",
    icon: <Instagram />,
  },
  {
    type: "whatsapp",
    displayName: "WhatsApp",
    icon: <Whatsapp />,
  },
];

function PracticeAreaIcon() {
  return (
    <span
      className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#D6D6D6] text-[#8A8A8A]"
      aria-hidden
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
    </span>
  );
}

function ProfileLink({
  to,
  className = "",
  children,
}: {
  to?: string;
  className?: string;
  children: React.ReactNode;
}) {
  if (!to) {
    return <span className={className}>{children}</span>;
  }

  return (
    <Link to={to} className={`${PANEL_LINK_CLASS} ${className}`.trim()}>
      {children}
    </Link>
  );
}

function normalizeLanguageList(languageList: string[]): string[] {
  return languageList
    .map((language) => language.trim())
    .filter(Boolean)
    .filter(
      (language, index, all) =>
        all.findIndex(
          (entry) => entry.toLowerCase() === language.toLowerCase(),
        ) === index,
    );
}

type NetworkingMapPersonPanelProps = {
  node: NetworkingMapNode;
};

export default function NetworkingMapPersonPanel({
  node,
}: NetworkingMapPersonPanelProps) {
  const {
    name,
    role,
    firm,
    firmLogo,
    personProfileUrl,
    firmProfileUrl,
    avatar,
    affinityPercent,
    jurisdictionLabel,
    location,
    matchingAreasOfPractice = [],
    languages = [],
    commonEvents = [],
    email,
    socialNetworks,
  } = node.data;

  const [specialtiesExpanded, setSpecialtiesExpanded] = useState(false);

  const matchingLanguages = useMemo(
    () => normalizeLanguageList(languages),
    [languages],
  );

  const levelLabel =
    affinityPercent != null
      ? getBandForPercent(affinityPercent).label
      : null;

  const hiddenSpecialtyCount = Math.max(
    0,
    matchingAreasOfPractice.length - VISIBLE_SPECIALTY_COUNT,
  );
  const visibleSpecialties = specialtiesExpanded
    ? matchingAreasOfPractice
    : matchingAreasOfPractice.slice(0, VISIBLE_SPECIALTY_COUNT);

  const handleCopyEmail = async () => {
    if (!email) {
      return;
    }

    try {
      await navigator.clipboard.writeText(email);
      toastSuccess("Email address copied to clipboard.");
    } catch {
      toastSuccess("Could not copy email. Please copy it manually.");
    }
  };

  return (
    <aside
      className="relative flex w-full min-w-0 max-w-full shrink-0 flex-col items-start overflow-y-auto rounded-lg border border-[#D6D6D6] bg-white p-4 text-left sm:p-5 lg:max-h-[calc(100vh-12rem)] lg:w-[min(100%,380px)] lg:overflow-y-auto xl:w-[400px]"
      aria-label={`${name} details`}
    >
      <div className="flex w-full items-start gap-4">
        <div className="flex shrink-0 flex-col items-center gap-2">
          <ProfileLink to={personProfileUrl}>
            <div
              className="h-20 w-20 overflow-hidden rounded-full bg-slate-300 bg-cover bg-center sm:h-[88px] sm:w-[88px]"
              style={avatar ? { backgroundImage: `url("${avatar}")` } : undefined}
              aria-hidden
            />
          </ProfileLink>
          {levelLabel && (
            <span className="rounded-lg bg-[#BC3323] px-2 py-0.5 font-['Roboto'] text-xs font-medium leading-4 text-white">
              {levelLabel}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <ProfileLink to={personProfileUrl}>
              <h2 className="font-['Roboto'] text-base font-semibold leading-5 text-gray-font sm:text-lg">
                {name}
              </h2>
            </ProfileLink>
            {role && (
              <span className="rounded-lg bg-[#EBEBEB] px-2 py-0.5 font-['Roboto'] text-xs font-medium leading-4 text-gray-font">
                {role}
              </span>
            )}
          </div>

          <div className="mt-3 space-y-2">
            {firm && (
              <ProfileLink
                to={firmProfileUrl}
                className="flex items-start gap-2 font-['Roboto'] text-sm leading-5 text-gray-font"
              >
                {firmLogo ? (
                  <img
                    src={firmLogo}
                    alt=""
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <Global className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                )}
                <span className="min-w-0 break-words">{firm}</span>
              </ProfileLink>
            )}

            {jurisdictionLabel && (
              <ProfileLink
                to={firmProfileUrl}
                className="flex flex-wrap items-center gap-1 font-['Roboto'] text-sm leading-5 text-gray-font"
              >
                <Global className="h-4 w-4 shrink-0 text-gray-font" />
                <span className="font-semibold">Jurisdiction:</span>
                <span>{jurisdictionLabel}</span>
              </ProfileLink>
            )}

            {location && (
              <ProfileLink
                to={firmProfileUrl}
                className="flex items-start gap-2 font-['Roboto'] text-sm leading-5 text-gray-font"
              >
                <LocationPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-font" />
                <span className="min-w-0 break-words">{location}</span>
              </ProfileLink>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 w-full space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-['Roboto'] text-sm font-semibold leading-5 text-gray-font">
            Social Media:
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {SOCIAL_ITEMS.map((item) => {
              const href = resolveSocialExternalUrl(
                getSocialUrl(socialNetworks, item.displayName),
                item.displayName,
              );
              const className = `inline-flex h-7 w-7 items-center justify-center ${
                href
                  ? "text-gray-font hover:text-primary"
                  : "cursor-not-allowed text-gray-300"
              }`;

              if (!href) {
                return (
                  <span
                    key={item.type}
                    className={className}
                    aria-label={item.displayName}
                    aria-disabled="true"
                  >
                    <span className="h-4 w-4 shrink-0 fill-current">
                      {item.icon}
                    </span>
                  </span>
                );
              }

              return (
                <a
                  key={item.type}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                  aria-label={`${item.displayName} profile`}
                >
                  <span className="h-4 w-4 shrink-0 fill-current">
                    {item.icon}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        {email && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="min-w-0 break-all font-['Roboto'] text-sm leading-5 text-gray-font">
              <span className="font-semibold">Email:</span> {email}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#0070CD] text-white transition-opacity hover:opacity-90"
                aria-label="Send email"
                onClick={() => {
                  window.location.href = `mailto:${email}`;
                }}
              >
                <Email className="h-3 w-3" />
              </button>
              <button
                type="button"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#0070CD] text-white transition-opacity hover:opacity-90"
                aria-label="Copy email address"
                onClick={handleCopyEmail}
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      <section className={PANEL_SECTION_BOX}>
        <h3 className="font-['Roboto'] text-sm font-semibold leading-5 text-gray-font">
          Matching Areas of Practice
        </h3>
        {matchingAreasOfPractice.length > 0 ? (
          <>
            <ul className="w-full space-y-2">
              {visibleSpecialties.map((specialty) => (
                <li
                  key={specialty}
                  className="flex items-start gap-2 font-['Roboto'] text-sm leading-5 text-gray-font"
                >
                  <PracticeAreaIcon />
                  <span className="min-w-0 break-words">{specialty}</span>
                </li>
              ))}
            </ul>
            {hiddenSpecialtyCount > 0 && (
              <button
                type="button"
                onClick={() => setSpecialtiesExpanded((expanded) => !expanded)}
                className="ml-auto inline-flex items-center gap-1 font-['Roboto'] text-sm font-medium text-[#0070CD]"
              >
                {specialtiesExpanded
                  ? "View less"
                  : `View +${hiddenSpecialtyCount} more`}
                <ChevronDown
                  className="h-4 w-4"
                  rotation={specialtiesExpanded ? "rotate(180deg)" : undefined}
                />
              </button>
            )}
          </>
        ) : (
          <p className="font-['Roboto'] text-sm leading-5 text-gray-font">
            No matching areas of practice.
          </p>
        )}
      </section>

      <section className={PANEL_SECTION_BOX}>
        <h3 className="font-['Roboto'] text-sm font-semibold leading-5 text-gray-font">
          Matching Languages
        </h3>
        {matchingLanguages.length > 0 ? (
          <div className="flex w-full flex-wrap gap-4">
            {matchingLanguages.map((language) => (
              <div
                key={language}
                className="flex min-w-0 items-center gap-2 font-['Roboto'] text-sm leading-5 text-gray-font"
              >
                <NetworkingMapLanguageIcon language={language} catalog={[]} />
                <span className="min-w-0 break-words">{language}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="font-['Roboto'] text-sm leading-5 text-gray-font">
            No matching languages in common.
          </p>
        )}
      </section>

      <section className={PANEL_SECTION_BOX}>
        <h3 className="font-['Roboto'] text-sm font-semibold leading-5 text-gray-font">
          Matching Events
        </h3>
        {commonEvents.length > 0 ? (
          <ul className="w-full space-y-3">
            {commonEvents.map((event) => (
              <li key={`${event.title}-${event.locationLabel ?? ""}-${event.dateLabel ?? ""}`}>
                <p className="font-['Roboto'] text-base font-bold leading-normal text-[#474F56]">
                  {event.title}
                </p>
                {(event.locationLabel || event.dateLabel) && (
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 font-['Roboto'] text-[13px] font-normal leading-normal text-[#474F56]">
                    {event.locationLabel && <span>{event.locationLabel}</span>}
                    {event.dateLabel && <span>{event.dateLabel}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-['Roboto'] text-sm leading-5 text-gray-font">
            No matching events in common.
          </p>
        )}
      </section>
    </aside>
  );
}
